import { useTranslation } from "@/i18n"
import { SORT_MODES, type SortMode } from "@/nostr/api/filters/filterEngine"
import { TIME_RANGES, type TimeRange } from "@/nostr/api/filters/timeFilter"
import { FilterChipGroup, FilterField, FilterSection, type FilterCategoryProps } from "./FilterSection"

const TIME_LABELS: Record<TimeRange, string> = {
  all: "All time",
  hour: "Last hour",
  day: "Last 24 hours",
  week: "Last 7 days",
  month: "Last 30 days",
  custom: "Custom range",
}

const TIME_OPTIONS = TIME_RANGES.map((value) => ({ value, label: TIME_LABELS[value] }))

const SORT_LABELS: Record<SortMode, string> = {
  latest: "Latest",
  oldest: "Oldest",
  likes: "Most liked",
  reposts: "Most reposted",
  replies: "Most discussed",
  engagement: "Most engaged",
}

const pad = (value: number) => String(value).padStart(2, "0")

/** `created_at` seconds -> `YYYY-MM-DD` in the reader's timezone. */
function toDateInput(seconds?: number): string {
  if (!seconds) return ""
  const date = new Date(seconds * 1000)
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function boundOf(value: string, endOfDay: boolean): number | undefined {
  if (!value) return undefined
  const date = new Date(`${value}T${endOfDay ? "23:59:59" : "00:00:00"}`)
  return Number.isNaN(date.getTime()) ? undefined : Math.floor(date.getTime() / 1000)
}

export interface TimeEngagementFiltersProps extends FilterCategoryProps {
  /** Shown because the counts are observed, not authoritative. */
  engagementNote?: string
}

/**
 * TIME & ENGAGEMENT: range, custom dates, thresholds and sorting.
 *
 * Sorting lives here but is *not* a filter: it reorders the same list, so it is
 * never counted as an active filter.
 */
export function TimeEngagementFilters({
  value,
  onChange,
  onReset,
  engagementNote,
}: TimeEngagementFiltersProps) {
  const { t } = useTranslation()

  const setThreshold = (
    field: "minLikes" | "minReposts" | "minReplies" | "minTotalEngagement",
    raw: string,
  ) => {
    const parsed = Number.parseInt(raw, 10)
    onChange({ [field]: Number.isFinite(parsed) && parsed > 0 ? parsed : 0 })
  }

  return (
    <FilterSection id="filter-section-time" title={t("Time & engagement")} onReset={onReset}>
      <FilterField label={t("Time range")}>
        <FilterChipGroup
          options={TIME_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }))}
          selected={[value.timeRange]}
          onToggle={(range) => onChange({ timeRange: range })}
          ariaLabel={t("Time range")}
        />
      </FilterField>

      {value.timeRange === "custom" && (
        <div className="filter-grid">
          <FilterField label={t("From")}>
            <input
              type="date"
              value={toDateInput(value.customRange.start)}
              onChange={(event) =>
                onChange({
                  customRange: { ...value.customRange, start: boundOf(event.target.value, false) },
                })
              }
              aria-label={t("From")}
            />
          </FilterField>
          <FilterField label={t("To")}>
            <input
              type="date"
              value={toDateInput(value.customRange.end)}
              onChange={(event) =>
                onChange({
                  customRange: { ...value.customRange, end: boundOf(event.target.value, true) },
                })
              }
              aria-label={t("To")}
            />
          </FilterField>
        </div>
      )}

      <div className="filter-grid">
        <FilterField label={t("Minimum likes")}>
          <input
            type="number"
            min={0}
            step={1}
            value={value.minLikes || ""}
            placeholder="0"
            onChange={(event) => setThreshold("minLikes", event.target.value)}
            aria-label={t("Minimum likes")}
          />
        </FilterField>
        <FilterField label={t("Minimum reposts")}>
          <input
            type="number"
            min={0}
            step={1}
            value={value.minReposts || ""}
            placeholder="0"
            onChange={(event) => setThreshold("minReposts", event.target.value)}
            aria-label={t("Minimum reposts")}
          />
        </FilterField>
        <FilterField label={t("Minimum replies")}>
          <input
            type="number"
            min={0}
            step={1}
            value={value.minReplies || ""}
            placeholder="0"
            onChange={(event) => setThreshold("minReplies", event.target.value)}
            aria-label={t("Minimum replies")}
          />
        </FilterField>
        <FilterField label={t("Minimum total engagement")}>
          <input
            type="number"
            min={0}
            step={1}
            value={value.minTotalEngagement || ""}
            placeholder="0"
            onChange={(event) => setThreshold("minTotalEngagement", event.target.value)}
            aria-label={t("Minimum total engagement")}
          />
        </FilterField>
      </div>

      {engagementNote && <p className="muted small">{engagementNote}</p>}

      <FilterField label={t("Sort by")}>
        <select
          value={value.sort}
          onChange={(event) => onChange({ sort: event.target.value as SortMode })}
          aria-label={t("Sort by")}
        >
          {SORT_MODES.map((mode) => (
            <option key={mode} value={mode}>
              {t(SORT_LABELS[mode])}
            </option>
          ))}
        </select>
      </FilterField>
    </FilterSection>
  )
}
